import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Logo from "../../assets/logo.svg";
import { theme } from "../../common/utils/theme";
import type { RootState } from "../../redux/store/store";
import Navbar from "./Navbar";
import { NavbarOptions } from "./widget/constants";

const MainLayout = (props: React.PropsWithChildren) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { selectedModule } = useSelector((store: RootState) => store?.module);
  const menuOptions = NavbarOptions[selectedModule]?.title || "";
  const [firstName, ...lastName] = menuOptions?.split(" ");
  const isProfilePage = location.pathname === "/profile";

  const isMobileOrTablet = useMediaQuery((theme: any) =>
    theme.breakpoints.down("md"),
  );
  const isLargeDesktop = useMediaQuery((theme: any) =>
    theme.breakpoints.up("xl"),
  );

  useEffect(() => {
    if (isMobileOrTablet) setMobileOpen(false);
  }, [isMobileOrTablet]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        position: "relative",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: "#03222F",
          boxShadow: "none",
          p: "0px !important",
          height: {
            xs: "4.5rem",
            sm: "4rem",
            md: "3.625rem",
            lg: "3.625rem",
            xl: "3.75rem",
          },
          minHeight: {
            xs: "4.5rem",
            sm: "4rem",
            md: "3.625rem",
            lg: "3.625rem",
            xl: "3.75rem",
          },
        }}
      >
        <Toolbar sx={{ p: "0px !important", width: "100%" }}>
          <Grid
            container
            width="100%"
            alignItems="center"
            sx={{
              display: "flex",
              pl: {
                xs: 1.5,
                sm: 1.75,
                md: 2,
                lg: 2,
                xl: 2.25,
              },
              pr: {
                xs: 1,
                sm: 1.5,
                md: 2,
                lg: 2,
                xl: 2.25,
              },
              minHeight: "inherit",
            }}
            display={"flex"}
            flexDirection={"row"}
          >
            <Grid
              size="auto"
              alignItems="center"
              display={"flex"}
              flexDirection={"row"}
            >
              <img
                src={Logo || "/placeholder.svg"}
                alt="Logo"
                style={{
                  height: isLargeDesktop ? "50px" : "48px",
                  width: isLargeDesktop ? "104px" : "100px",
                  alignSelf: "center",
                  objectFit: "contain",
                  maxWidth: "100%",
                }}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mt: isLargeDesktop ? "8px" : "6px",
                  mx: isLargeDesktop ? 1.25 : 1,
                  height: isLargeDesktop ? "42px" : "40px",
                  backgroundColor: theme?.palette?.grey?.[100],
                  color: theme?.palette?.grey?.[100],
                }}
              />
              <Grid>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme?.palette?.common?.white,
                    fontSize: isLargeDesktop ? "1.625rem" : "1.5rem",
                    lineHeight: isLargeDesktop ? 1.4 : 1.334,
                  }}
                >
                  {firstName}
                </Typography>

                {lastName?.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.common?.white,
                      fontSize: isLargeDesktop ? "0.9375rem" : "0.875rem",
                      lineHeight: isLargeDesktop ? 1.5 : 1.43,
                    }}
                  >
                    {lastName.join(" ")}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid
              container
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
              sx={{
                pl: {
                  xs: 1,
                  sm: 1.5,
                  md: 2,
                  lg: 1,
                  xl: 2,
                },
                pr: {
                  xs: 1,
                  sm: 1.5,
                  md: 2,
                  lg: 2,
                  xl: 0,
                },
                flex: 1,
                minWidth: 0,
              }}
              width="100%"
            >
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                <Navbar selectedModule={selectedModule} />
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          pt: {
            xs: "4.5rem",
            sm: "4rem",
            md: "3.625rem",
            lg: "3.625rem",
            xl: "3.75rem",
          },
          height: {
            xs: "calc(100vh - 4.5rem)",
            sm: "calc(100vh - 4rem)",
            md: "calc(100vh - 3.625rem)",
            lg: "calc(100vh - 3.625rem)",
            xl: "calc(100vh - 3.75rem)",
          },
          minHeight: {
            xs: "calc(100vh - 4.5rem)",
            sm: "calc(100vh - 4rem)",
            md: "calc(100vh - 3.625rem)",
            lg: "calc(100vh - 3.625rem)",
            xl: "calc(100vh - 3.75rem)",
          },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        {props.children}
        <CssBaseline />
      </Box>
    </Box>
  );
};

export default MainLayout;
